import ArrowBackIosNew from '@mui/icons-material/ArrowBackIosNew';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { type ReactElement, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router';

import {
  type ReasonCode,
  type Subscription,
  type SubscriptionId,
  SubscriptionIdSchema,
  type SubscriptionStatus,
} from '../../api';
import { ChannelAvatar } from '../../components/ChannelAvatar';
import { StatusChip } from '../../components/StatusChip';
import { reasonMessage } from '../../i18n';
import type { ApiError } from '../../lib/api/api-client';

import { ChannelSettingsForm } from './ChannelSettingsForm';
import { PauseResumeControl } from './PauseResumeControl';
import { StopCopyingControl } from './StopCopyingControl';
import { statusNotice } from './channel-status';
import { limitsAccountFromMe } from './daily-limit';
import { type SettingsScreenData, useSettingsData } from './settings-api';

/**
 * Screen E7 · Channel settings (specs/M2-channel-settings/spec.md; US-10 to US-12), route
 * `CHANNEL_SETTINGS_ROUTE`. Loads the settings and `GET /me`, then shows the status of the
 * channel, {@link ChannelSettingsForm} and the pause or resume control (M3, US-13).
 */
export function ChannelSettingsScreen(): ReactElement {
  const { subscriptionId } = useParams();
  const parsed = SubscriptionIdSchema.safeParse(subscriptionId);
  if (!parsed.success) {
    // A malformed link never reaches the API: the same text as an unknown subscription.
    return (
      <Layout name={null} seed="">
        <Failure reasonCode="NOT_FOUND" />
      </Layout>
    );
  }
  // Keyed by subscription: going from one channel's E7 to another's (same route, new param)
  // starts from that channel's saved values, never the previous channel's draft (I7).
  return <ChannelSettingsView key={parsed.data} subscriptionId={parsed.data} />;
}

function ChannelSettingsView({
  subscriptionId,
}: {
  readonly subscriptionId: SubscriptionId;
}): ReactElement {
  const { t } = useTranslation();
  // A failed reload keeps the last data (`useApiQuery`, M7 R7): the form and its "Saved" notice
  // stay mounted after a save, and the failure shows as a banner (`refreshError`).
  const query = useSettingsData(subscriptionId);
  const { data, refetch } = query;
  // The subscription a pause or resume returned (M3). It is shown at once, over the data E7
  // shows when the answer arrives (`shownOver`, that very object); the next successful reload of
  // the settings and `GET /me` takes over (M3 §5: both are refreshed), since it brings a new
  // object. A failed reload keeps the same object, so the returned state stays shown. The data is
  // read from a ref when the answer arrives, not from the render that sent the request: a save
  // reload landing while the pause is in flight must not hide the paused state the API returned.
  const shownData = useRef(data);
  useEffect(() => {
    shownData.current = data;
  }, [data]);
  const [changed, setChanged] = useState<{
    readonly subscription: Subscription;
    readonly shownOver: SettingsScreenData | undefined;
  } | null>(null);
  const onChanged = useCallback(
    (subscription: Subscription) => {
      setChanged({ subscription, shownOver: shownData.current });
      refetch();
    },
    [refetch],
  );
  if (data === undefined) {
    return (
      <Layout name={null} seed={subscriptionId}>
        {query.status === 'error' ? (
          <Failure
            reasonCode={query.error.reasonCode}
            error={query.error}
            onRetry={query.refetch}
          />
        ) : (
          <Box
            role="status"
            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: 'text.secondary', py: 2 }}
          >
            <CircularProgress size={20} aria-hidden />
            {t('settings.loading')}
          </Box>
        )}
      </Layout>
    );
  }
  const { settings, me } = data;
  const current: ChannelState =
    changed !== null && changed.shownOver === data ? changed.subscription : settings;
  return (
    <Layout name={channelName(data)} seed={subscriptionId}>
      {query.refreshError !== undefined && (
        <Failure
          reasonCode={query.refreshError.reasonCode}
          error={query.refreshError}
          onRetry={query.refetch}
        />
      )}
      <StatusBlock state={current} />
      <ChannelSettingsForm
        subscriptionId={subscriptionId}
        active={settings.active?.settings ?? null}
        activeVersion={settings.active?.version ?? null}
        recommended={settings.recommended}
        limitsAccount={limitsAccountFromMe(me, settings.accountLimits)}
        onSaved={query.refetch}
      />
      <PauseResumeControl
        subscriptionId={subscriptionId}
        status={current.status}
        onChanged={onChanged}
      />
      {/* S9 R16 (Q121): the canvas E12 footer sends the trader here to stop copying. */}
      <StopCopyingControl subscriptionId={subscriptionId} channelName={channelName(data)} />
    </Layout>
  );
}

function channelName({ settings, me }: SettingsScreenData): string | null {
  return (
    me.channels.find((channel) => channel.subscriptionId === settings.subscriptionId)
      ?.channelName ?? null
  );
}

/** Back arrow, channel avatar and name, "Settings for this channel only" (mockup 6). */
function Layout({
  name,
  seed,
  children,
}: {
  readonly name: string | null;
  readonly seed: string;
  readonly children: ReactNode;
}): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, pb: 2 }}>
      <Box component="header" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <IconButton
          aria-label={t('settings.back')}
          onClick={() => {
            // E7 opens from Home and from the Channels tab (R1): go back where the trader came
            // from. Opened directly (no history in the app), go to Home.
            void (location.key === 'default' ? navigate('/') : navigate(-1));
          }}
          sx={{ width: 44, height: 44, color: 'text.primary' }}
        >
          <ArrowBackIosNew fontSize="small" aria-hidden />
        </IconButton>
        {name !== null && <ChannelAvatar seed={seed} name={name} size={36} />}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            component="h1"
            sx={{ fontWeight: 600, fontSize: 15, overflowWrap: 'anywhere' }}
          >
            {name ?? t('settings.title')}
          </Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
            {t('settings.subtitle')}
          </Typography>
        </Box>
      </Box>
      {children}
    </Box>
  );
}

/** The channel's status and the cause of a suspension, from the settings GET or a pause/resume. */
interface ChannelState {
  readonly status: SubscriptionStatus;
  readonly suspensionReasonCode: ReasonCode | null;
}

/** The channel's state at the top of E7 (M2 §4, AC16; M3 §4). */
function StatusBlock({ state }: { readonly state: ChannelState }): ReactElement | null {
  const { t } = useTranslation();
  const notice = statusNotice(state.status, state.suspensionReasonCode);
  if (notice === null) return null;
  const message = notice.reasonCode === null ? null : reasonMessage(notice.reasonCode);
  return (
    <Alert
      severity={notice.tone === 'danger' ? 'error' : 'info'}
      icon={false}
      sx={{ alignItems: 'center' }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, alignItems: 'flex-start' }}>
        {notice.chip !== null && (
          <StatusChip tone={notice.tone === 'danger' ? 'danger' : 'neutral'}>
            {t(`settings.chip.${notice.chip}`)}
          </StatusChip>
        )}
        {message !== null && t(message.key, message.values)}
      </Box>
    </Alert>
  );
}

/** A load failure: only the translated reason, never a raw message (I9, I12). */
function Failure({
  reasonCode,
  error,
  onRetry,
}: {
  readonly reasonCode: ApiError['reasonCode'];
  readonly error?: ApiError;
  readonly onRetry?: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const message = reasonMessage(reasonCode, error?.params);
  return (
    <Alert
      severity="error"
      action={
        onRetry === undefined ? undefined : (
          <Button color="inherit" size="small" onClick={onRetry} sx={{ minHeight: 36 }}>
            {t('settings.retry')}
          </Button>
        )
      }
    >
      {t(message.key, message.values)}
    </Alert>
  );
}
